const KEYS = {
  TICKETS: 'action_tickets',
  DAILY_COMPLETIONS: 'action_daily_completions',
  STREAK: 'action_streak',
  GAME_HISTORY: 'action_game_history',
  USER: 'action_user',
  SETTINGS: 'action_settings',
  SAVED_GAME: 'action_saved_game',
  PERM_THEMES: 'action_perm_themes',
}

function safeGet(key, fallback) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {}
}

export const getTickets = () => safeGet(KEYS.TICKETS, 0)
export const persistTickets = (n) => safeSet(KEYS.TICKETS, n)

export const getDailyCompletions = () => safeGet(KEYS.DAILY_COMPLETIONS, {})
export const getDailyCompletionForDate = (date) => getDailyCompletions()[date] ?? null

export function saveDailyCompletion(date, data) {
  const all = getDailyCompletions()
  all[date] = { ...data, completedAt: new Date().toISOString() }
  safeSet(KEYS.DAILY_COMPLETIONS, all)
}

/**
 * Merge entries from Firestore (source of truth) into localStorage.
 * Each entry overwrites any local-only state for that date — if Firestore
 * says a daily is completed, treat it as completed regardless of localStorage.
 * Firestore Timestamps are converted to ISO strings for consistency.
 */
export function mergeDailyCompletions(entries) {
  if (!entries || typeof entries !== 'object') return
  const all = getDailyCompletions()
  let mutated = false
  for (const [date, data] of Object.entries(entries)) {
    if (!data) continue
    // Normalize completedAt: Firestore Timestamp → ISO string
    let completedAtIso = null
    if (data.completedAt?.toDate) {
      try { completedAtIso = data.completedAt.toDate().toISOString() } catch {}
    } else if (typeof data.completedAt === 'string') {
      completedAtIso = data.completedAt
    } else if (data.completedAt?.seconds) {
      completedAtIso = new Date(data.completedAt.seconds * 1000).toISOString()
    }
    all[date] = {
      ...data,
      completedAt: completedAtIso || new Date().toISOString(),
    }
    mutated = true
  }
  if (mutated) safeSet(KEYS.DAILY_COMPLETIONS, all)
}

export const getStreak = () =>
  safeGet(KEYS.STREAK, { currentStreak: 0, longestStreak: 0, lastPlayedDate: null })

/** Write a full streak object straight to localStorage (used to hydrate the
 *  streak from Firestore on sign-in). */
export const persistStreak = (streak) => safeSet(KEYS.STREAK, streak)

export function updateStreak(date) {
  const streak = getStreak()

  console.log('[streak:update] called', {
    date,
    lastPlayedDate: streak.lastPlayedDate,
    currentStreak: streak.currentStreak,
  })

  if (streak.lastPlayedDate === date) {
    console.log('[streak:update] already played today — no change')
    return streak
  }

  // Compute the calendar day before `date` ENTIRELY in local time so the
  // result matches getLocalDateStr() — which writes lastPlayedDate.
  //
  // The previous recipe (new Date("YYYY-MM-DD").setDate(getDate()-1).toISOString())
  // parsed as UTC, mutated as local, output as UTC — three different calendars
  // in one expression, which caused off-by-one resets at TZ-correlated dates.
  //
  // The all-local approach below is safe across DST transitions, month/year
  // boundaries, and fractional-offset timezones (UTC+5:30, UTC-3:30, etc.).
  const [yIn, mIn, dIn] = date.split('-').map(Number)
  const yesterday = new Date(yIn, mIn - 1, dIn)        // local midnight on `date`
  yesterday.setDate(yesterday.getDate() - 1)            // subtract one local day
  const yyyy = yesterday.getFullYear()
  const mm   = String(yesterday.getMonth() + 1).padStart(2, '0')
  const dd   = String(yesterday.getDate()).padStart(2, '0')
  const yesterdayStr = `${yyyy}-${mm}-${dd}`

  const willIncrement = streak.lastPlayedDate === yesterdayStr
  const current = willIncrement ? streak.currentStreak + 1 : 1

  console.log('[streak:update] comparison', {
    yesterdayStr,
    lastPlayedDate: streak.lastPlayedDate,
    match: willIncrement,
    streakBefore: streak.currentStreak,
    streakAfter: current,
  })

  if (!willIncrement) {
    console.warn(
      '[streak:update] RESET — lastPlayedDate did not match yesterday.',
      `Expected "${yesterdayStr}", got "${streak.lastPlayedDate}".`,
      'Check whether persistStreak was called with stale Firestore data on sign-in.',
    )
  }

  const updated = {
    currentStreak: current,
    longestStreak: Math.max(current, streak.longestStreak),
    lastPlayedDate: date,
  }
  safeSet(KEYS.STREAK, updated)

  // Surface diagnostic info to the caller when a reset occurred — but NEVER
  // persist it (already stored `updated` above). The call site at the daily
  // completion handler reads _resetInfo to write a streakResetDebug field to
  // Firestore so we can investigate user-reported reset bugs.
  if (!willIncrement) {
    return {
      ...updated,
      _resetInfo: {
        expected: yesterdayStr,
        got: streak.lastPlayedDate ?? null,
      },
    }
  }
  return updated
}

/**
 * Permanently-unlocked themes (new permanent-unlock economy).
 * Once a theme is earned via a 7-day streak milestone OR a 100-ticket
 * purchase, it stays in this list forever — streak resets don't remove it.
 * Baseline is always ['extra'] so that the default theme is never absent.
 */
export function getPermUnlockedThemes() {
  const raw = safeGet(KEYS.PERM_THEMES, null)
  if (!Array.isArray(raw)) return ['extra']
  return raw.includes('extra') ? raw : ['extra', ...raw]
}

export function persistPermUnlockedThemes(ids) {
  const arr = Array.isArray(ids) ? ids : []
  const withBaseline = arr.includes('extra') ? arr : ['extra', ...arr]
  safeSet(KEYS.PERM_THEMES, withBaseline)
}

export const getGameHistory = () => safeGet(KEYS.GAME_HISTORY, [])

export function addGameHistory(entry) {
  const history = getGameHistory()
  history.unshift(entry)
  if (history.length > 100) history.length = 100
  safeSet(KEYS.GAME_HISTORY, history)
}

// Difficulty ratings — keyed by puzzle date
const RATINGS_KEY = 'action_daily_ratings'
export const getDailyRatings = () => safeGet(RATINGS_KEY, {})
export const getDailyRating = (date) => getDailyRatings()[date] ?? null
export function saveDailyRating(date, rating) {
  const all = getDailyRatings()
  all[date] = rating
  safeSet(RATINGS_KEY, all)
}

// User profile
const defaultUser = () => ({
  username: null,
  avatar: null,
  joinedDate: new Date().toISOString().slice(0, 10),
})

export function getUser() {
  const u = safeGet(KEYS.USER, null)
  if (!u) {
    const fresh = defaultUser()
    safeSet(KEYS.USER, fresh)
    return fresh
  }
  return u
}

export function saveUser(patch) {
  const current = getUser()
  const next = { ...current, ...patch }
  safeSet(KEYS.USER, next)
  return next
}

// App settings
const defaultSettings = () => ({ destinationHighlight: true })

export function getSettings() {
  return { ...defaultSettings(), ...safeGet(KEYS.SETTINGS, {}) }
}

export function saveSettings(patch) {
  const next = { ...getSettings(), ...patch }
  safeSet(KEYS.SETTINGS, next)
  return next
}

// Saved game (Single Player / Multiplayer H2H)
export const getSavedGame = () => safeGet(KEYS.SAVED_GAME, null)
export const persistSavedGame = (game) => safeSet(KEYS.SAVED_GAME, game)
export function clearSavedGame() {
  try { localStorage.removeItem(KEYS.SAVED_GAME) } catch {}
}

// Active paid game flag — set immediately on ticket deduction, cleared on completion/abandon
const ACTIVE_PAID_KEY = 'action_active_paid_game'
export const getActivePaidGame = () => safeGet(ACTIVE_PAID_KEY, null)
export const setActivePaidGame = (data) => safeSet(ACTIVE_PAID_KEY, data)
export function clearActivePaidGame() {
  try { localStorage.removeItem(ACTIVE_PAID_KEY) } catch {}
}

// One-time migration: divide old move counts by 2 to convert to hops
const HOPS_MIGRATION_KEY = 'action_hops_migrated_v1'
export function migrateMovesToHops() {
  try {
    if (localStorage.getItem(HOPS_MIGRATION_KEY)) return
    // Migrate daily completions
    const completions = getDailyCompletions()
    const migratedCompletions = {}
    for (const [date, data] of Object.entries(completions)) {
      migratedCompletions[date] = { ...data, moves: Math.round((data.moves || 0) / 2) }
    }
    safeSet(KEYS.DAILY_COMPLETIONS, migratedCompletions)
    // Migrate game history
    const history = getGameHistory()
    const migratedHistory = history.map(h => ({ ...h, moves: Math.round((h.moves || 0) / 2) }))
    safeSet(KEYS.GAME_HISTORY, migratedHistory)
    localStorage.setItem(HOPS_MIGRATION_KEY, '1')
  } catch {}
}
